import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { FRAME_SOLID2 } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { hexToTint } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

/**
 * SystemsContext — the shared plumbing of the systems department and its
 * phase/line classes: the ParticlePool('systems') free-list (linear scan
 * from the pool start, same reuse pattern as the reference), the lane
 * helpers (deactivateAllBehaviours / position.set-hard /
 * SystemsParticleProcessor.setSystemsStart), HomeParticle.activateAsTrail
 * as a lane arm, and the ParticleTrail fade pass over the trail lanes.
 */
export class SystemsContext {
	public readonly core: ParticleCore;
	public readonly range: PoolRange;
	public readonly deps: DepartmentDeps;

	//ParticlePool('systems') — the linear-scan free-list (lane - range.start):
	private _pooled: Uint8Array;

	constructor(core: ParticleCore, deps: DepartmentDeps) {
		this.core = core;
		this.deps = deps;
		this.range = core.pool('systems');

		this._pooled = new Uint8Array(this.range.count);
	}

	//Pool free-list
	//-----------------------------------------------------------------------------------------------

	/** ParticlePool.get('systems'): first !pooled lane, scan from the start. */
	public allocLane(): number {
		for (let n = 0; n < this.range.count; n++) {
			if (!this._pooled[n]) {
				this._pooled[n] = 1;
				return this.range.start + n;
			}
		}
		return -1;
	}

	/** ParticlePool.free. */
	public freeLane(lane: number): void {
		this._pooled[lane - this.range.start] = 0;
	}

	/** The scene-stop sweep clears every pooled flag. */
	public freeAll(): void {
		this._pooled.fill(0);
	}

	//Shared lane helpers (verbatim reference ports)
	//-----------------------------------------------------------------------------------------------

	/** deactivateAllBehaviours(true). */
	public deactivateAll(lane: number): void {
		const core = this.core;
		core.behaviourMask[lane] =
			(core.behaviourMask[lane] & (BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X)) | BEHAVIOUR_PROGRESS_PARALLAX;
		core.trailOn[lane] = 0;
	}

	/** position.set({x, y}, true) — rel + HARD px write, honouring the
	 *  lane's disableTimelineX mode (parallaxPower is 0 on this pool, so the
	 *  timeline branch's drag offset is zero). */
	public setHardPosition(lane: number, x: number, y: number): void {
		const core = this.core;
		const sizes = this.deps.getSizes();

		core.relX[lane] = x;
		core.relY[lane] = y;

		const absX = x * sizes.virtualWidth;
		let targetX: number;
		if (core.behaviourMask[lane] & BEHAVIOUR_DISABLE_TIMELINE_X) {
			targetX = absX;
		} else {
			const drag = (x - 0.5 - (this.deps.context.progress - 0.5)) * core.parallaxPower[lane] * (sizes.virtualWidth * 0.5);
			targetX = absX + this.deps.context.containerX + drag;
		}

		core.tx[lane] = core.x[lane] = core.pTx[lane] = targetX;
		core.ty[lane] = core.y[lane] = core.pTy[lane] = y * sizes.innerHeight;
	}

	/** SystemsParticleProcessor.setSystemsStart, lane-for-line. */
	public setSystemsStart(lane: number): void {
		const core = this.core;
		//activate():
		core.stateFlags[lane] |= FLAG_ACTIVE;
		//orbitPath.active = false:
		core.behaviourMask[lane] &= ~BEHAVIOUR_PATH_ORBIT;
		//position.disableTimelineX = true:
		core.behaviourMask[lane] |= BEHAVIOUR_DISABLE_TIMELINE_X;
		//repell.randomisePower(1e-7, 1e-7) — roll kept (usually overwritten):
		core.repellPower[lane] = 0.0000001 + Math.random() * 0.0000001;
	}

	/**
	 * HomeParticle.activateAsTrail, lane-for-line (child <- head). The head's
	 * CURRENT alpha/scale values seed the dot; setSystemsStart's repell roll
	 * is overwritten by the PUSH block like the reference.
	 */
	public activateAsTrail(
		lane: number,
		headLane: number,
		lifetimeFramesBase: number,
		lifetimeFramesSeed: number,
		atX: number,
		atY: number,
		inSafeZone: boolean,
		scaleOffParent: number,
		colour: string
	): void {
		const core = this.core;

		this.deactivateAll(lane);

		core.stateFlags[lane] |= FLAG_ACTIVE;

		this.setHardPosition(lane, atX, atY);

		//Match the colour of the emitter:
		core.tint[lane] = hexToTint(colour);

		//The trail item (alpha/scale seeded off the head, lifetime rolled):
		const alpha = inSafeZone ? core.alpha[headLane] * 0.4 : core.alpha[headLane];
		const scale = core.scale[headLane] * scaleOffParent;
		const scaleLifetime = inSafeZone ? 0.2 : 1;
		const lifetime = Math.round(lifetimeFramesBase * scaleLifetime + Math.random() * (lifetimeFramesSeed * scaleLifetime));

		core.trailOn[lane] = 1;
		core.trailAlpha[lane] = alpha;
		core.trailScale[lane] = scale;
		core.trailStep[lane] = alpha / lifetime;

		//setVectorStyleMovement(true) — default rates:
		writeVectorConfig(core, lane, VEC_X, true);
		writeVectorConfig(core, lane, VEC_Y, true);

		this.setSystemsStart(lane);

		//repell: systemsMouse (the facade's systems repell centre), PUSH 50:
		if (inSafeZone) {
			core.behaviourMask[lane] &= ~BEHAVIOUR_REPELL;
		} else {
			core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		}
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 50;
		core.repellPower[lane] = 30 + Math.random() * 200;

		//changeParticle('solid2.png'):
		core.textureId[lane] = FRAME_SOLID2;

		//particlePhysics.active = false:
		core.behaviourMask[lane] &= ~BEHAVIOUR_PHYSICS;

		//position on (already), setPosition is a sprite-write flag (no-op
		//here), orbit off (already). setEase({y: 2+r*2, x: 2+r*2}) — the y
		//roll draws first (object-literal order, RNG stream kept):
		core.easeY[lane] = 2 + Math.random() * 2;
		core.easeX[lane] = 2 + Math.random() * 2;
		core.easeLerpFramesX[lane] = 0;
		core.easeLerpFramesY[lane] = 0;
	}

	/** ParticleTrail.tick over the trail lanes: alpha fades by step, scale is
	 *  written constant (the reference decays its scaleStep, not the scale —
	 *  bug kept); at zero the lane frees but STAYS active at alpha 0. */
	public tickTrails(): void {
		const core = this.core;
		const deltaTime = this.deps.context.deltaTime;

		for (let i = this.range.start; i < this.range.end; i++) {
			if (!core.trailOn[i]) {
				continue;
			}

			core.trailAlpha[i] -= core.trailStep[i] * deltaTime;

			if (core.trailAlpha[i] <= 0) {
				core.trailAlpha[i] = 0;
				core.trailOn[i] = 0;
				this.freeLane(i);
			}

			core.alpha[i] = core.tAlpha[i] = core.trailAlpha[i];
			core.scale[i] = core.tScale[i] = core.trailScale[i];
		}
	}
}
